import { useCallback, useEffect, useState } from 'react'
import { api } from './api'
import type { Person } from './types'

/** The organization's saved people (who applied it, who did the work), alphabetically. */
export function usePeople(orgId: string | undefined) {
  const [people, setPeople] = useState<Person[]>([])

  const reload = useCallback(async () => {
    if (!orgId) return
    try {
      setPeople(await api.get<Person[]>(`/orgs/${orgId}/people`))
    } catch {
      setPeople([]) // suggestions only; typing still works
    }
  }, [orgId])

  useEffect(() => {
    reload()
  }, [reload])

  return { people, reload }
}

/** Adds a name to the list. A name already there (any capitalization) comes back as it is. */
export const addPerson = (orgId: string, name: string) => api.post<Person>(`/orgs/${orgId}/people`, { name: name.trim() })

/**
 * Puts the names an entry used on the saved list, so next time they are a tap. Best effort: the
 * entry is already recorded, and a name that can't be saved is still on it.
 */
export async function rememberPeople(orgId: string, names: (string | undefined)[]): Promise<void> {
  const unique = [...new Map(names.map((n) => n?.trim()).filter((n): n is string => !!n).map((n) => [n.toLowerCase(), n])).values()]
  await Promise.allSettled(unique.map((n) => addPerson(orgId, n)))
}
